import React, { useContext, useState } from 'react'
import moment from 'moment'

import { OrderIncentive, IncentivesResult, redeemIncentive } from './api/incentives'
import {
  CustomerMarketingResult,
  CustomerProfile,
  CustomerLoyalty,
  OrderSnapshot,
  PastOrdersResponse,
  hasPastOrders,
  ReorderDueInfo,
  OrderAgainTriggerStatus,
  loadReorderDue,
  triggerOrderAgain,
} from './api/customer-marketing'
import { OrderCtx } from './model-order'
import { RestaurantCtx, Restaurant } from './model-restaurant'
import { Panel, Line } from './fragments/comp/panel'

const utils = require('views/order/components/utils')

// inserted_at etc. are UTC; convert to the viewer's local TZ then use relative wording
function rel(v: string | null | undefined): string {
  if (v == null || v === '') return ''
  const m = moment.utc(v)
  return m.isValid() ? m.local().fromNow() : ''
}
function localWhen(v: string | null | undefined): string {
  if (v == null || v === '') return ''
  const m = moment.utc(v)
  return m.isValid() ? m.local().format('LLL') : ''
}

function isPast(v: string | null | undefined): boolean {
  if (v == null || v === '') return false
  const m = moment.utc(v)
  return m.isValid() && m.isBefore(moment.utc())
}

function money(locale: string | undefined, dollars: number): string {
  if (typeof dollars !== 'number' || !isFinite(dollars)) return ''
  return locale ? utils.currency(locale, dollars * 100) : `$${dollars.toFixed(2)}`
}

function humanize(s: string | null | undefined): string {
  if (s == null || typeof s !== 'string') return ''
  return s.toLowerCase().replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
}

function pts(n: number): string {
  const x = typeof n === 'number' && isFinite(n) ? n : 0
  return `${x.toLocaleString()} pts`
}

// offer/discount/sent_offers mix strings and `{type, title, reasoning}` objects
function asText(v: any): string | null {
  if (v == null) return null
  if (typeof v === 'string') return v === '' ? null : v
  if (typeof v === 'number') return String(v)
  if (typeof v === 'object') return asText(v.title) || asText(v.name)
  return null
}

function offerLabel(o: OrderSnapshot): string | null {
  const inc = o.incentive
  const fromInc = asText(inc && inc.offer) || asText(inc && inc.discount)
  if (fromInc) return fromInc
  const sent = o.sent_offers || []
  if (sent.length === 0) return null
  const labels = sent.map(asText).filter((x: string | null): x is string => x != null)
  return labels.length > 0 ? labels.join(' · ') : null
}

// status at a glance, shown in the panel header: can the customer still use this offer?
function statusChip(i: OrderIncentive): JSX.Element {
  if (i.redeemed_at != null) {
    return (
      <span className="src-chip src-chip-info" title={`Redeemed ${localWhen(i.redeemed_at)}`}>
        Redeemed
      </span>
    )
  }
  if (i.expires_at != null && isPast(i.expires_at)) {
    return (
      <span className="src-chip src-chip-danger" title={`Expired ${localWhen(i.expires_at)}`}>
        Expired
      </span>
    )
  }
  return (
    <span
      className="src-chip src-chip-success"
      title={i.expires_at != null ? `Expires ${localWhen(i.expires_at)}` : 'No expiry'}>
      Available
    </span>
  )
}

// API type constants are programmer-speak; show restaurant staff plain words
// (raw constant stays in the row's tooltip)
const TYPE_LABELS: Record<string, string> = {
  dollar_discount: 'Dollar off',
  percent_discount: 'Percent off',
  meal_deal: 'Meal deal',
  item_deal: 'Item deal',
  loyalty_contract: 'Loyalty reward',
  simple_nudge: 'Nudge',
}

function typeLabel(type: string): string {
  if (!type) return ''
  return TYPE_LABELS[type] || type.replace(/_/g, ' ').replace(/^./, (c) => c.toUpperCase())
}

function canRedeem(i: OrderIncentive): boolean {
  return i.redeemed_at == null && (i.expires_at == null || !isPast(i.expires_at))
}

function IncentiveBody(props: { i: OrderIncentive; selfUuid?: string; onRedeem?: () => void }): JSX.Element {
  const { i, selfUuid } = props
  const recs: any[] = (i.fields && i.fields.recommended_items) || []
  const [redeeming, setRedeeming] = useState(false)
  const [redeemFailed, setRedeemFailed] = useState(false)

  const redeem = (): void => {
    if (!confirm('Mark this offer as redeemed?')) return
    setRedeeming(true)
    setRedeemFailed(false)
    redeemIncentive(i.id).then((v: OrderIncentive | null) => {
      setRedeeming(false)
      if (v == null) setRedeemFailed(true)
      else if (props.onRedeem) props.onRedeem()
    })
  }

  return (
    <li className="list-group-item">
      <div className="src-name mkt-title">{i.title}</div>
      <div className="src-tz mt-1">
        <span title={localWhen(i.inserted_at)}>sent {rel(i.inserted_at)}</span>
        {i.expires_at != null && (
          <span title={localWhen(i.expires_at)}>
            {' · '}
            {isPast(i.expires_at) ? `expired ${rel(i.expires_at)}` : `expires ${rel(i.expires_at)}`}
          </span>
        )}
      </div>
      {i.description != null && <div className="mt-2">{i.description}</div>}
      <div className="src-grid mt-2">
        <span className="src-k">Type</span>
        <span className="src-v" title={i.type}>
          {typeLabel(i.type)}
        </span>
        {recs.length > 0 && (
          <>
            <span className="src-k">Suggested</span>
            <span className="src-v">{recs.map((r: any) => r.name).join(' · ')}</span>
          </>
        )}
        {i.reasoning != null && (
          <>
            <span className="src-k">Why</span>
            <span className="src-v">{i.reasoning}</span>
          </>
        )}
        {i.from_order_uuid != null && i.from_order_uuid !== selfUuid && (
          <>
            <span className="src-k">From order</span>
            <span className="src-v">
              <a href={`#/orders/${i.from_order_uuid}`}>View order ↗</a>
            </span>
          </>
        )}
        {i.redeemed_at != null && (
          <>
            <span className="src-k">Redeemed</span>
            <span className="src-v" title={localWhen(i.redeemed_at)}>
              {rel(i.redeemed_at)}
              {/* redeemed_by may be a generated uuid (manual redeem) that leads to no order page — accepted per spec */}
              {i.redeemed_by != null && i.redeemed_by !== selfUuid && (
                <>
                  {' '}
                  <a href={`#/orders/${i.redeemed_by}`}>View order ↗</a>
                </>
              )}
            </span>
          </>
        )}
      </div>
      {canRedeem(i) && props.onRedeem != null && (
        <div className="src-actions">
          <button type="button" onClick={redeem} disabled={redeeming}>
            {redeeming ? 'Redeeming…' : 'Redeem'}
          </button>
        </div>
      )}
      {redeemFailed && <div className="src-tz mt-1">Couldn’t redeem offer</div>}
    </li>
  )
}

function typeChip(customerType: string): JSX.Element {
  const registered = (customerType || '').toLowerCase() === 'registered'
  return (
    <span className={registered ? 'src-chip src-chip-success' : 'src-chip src-chip-info'}>
      {humanize(customerType)}
    </span>
  )
}

function daysLabel(n: number): string {
  return n === 1 ? 'every 1 day' : `every ${n} days`
}

function ReorderDue(props: { orderUuid: string; onOfferSent?: () => void }): JSX.Element {
  const [info, setInfo] = useState<ReorderDueInfo | null>(null)
  const [sent, setSent] = useState<OrderAgainTriggerStatus | null>(null)
  const [dueLoading, setDueLoading] = useState(false)
  const [sendLoading, setSendLoading] = useState(false)
  const [dueFailed, setDueFailed] = useState(false)
  const [sendFailed, setSendFailed] = useState(false)

  const load = (): void => {
    setDueLoading(true)
    setDueFailed(false)
    loadReorderDue(props.orderUuid).then((v: ReorderDueInfo | null) => {
      setDueLoading(false)
      if (v == null) setDueFailed(true)
      else setInfo(v)
    })
  }

  const send = (): void => {
    if (!confirm('Send an order-again offer for this order?')) return
    setSendLoading(true)
    setSendFailed(false)
    triggerOrderAgain(props.orderUuid).then((v: OrderAgainTriggerStatus | null) => {
      setSendLoading(false)
      if (v == null) {
        setSendFailed(true)
        return
      }
      setSent(v)
      if (props.onOfferSent) props.onOfferSent()
    })
  }

  return (
    <div>
      {sent != null ? (
        <div className="src-grid mb-2">
          <span className="src-k">Sent</span>
          <span className="src-v" title={localWhen(sent.manually_triggered_at)}>
            {rel(sent.manually_triggered_at)} · check back shortly
          </span>
        </div>
      ) : (
        info != null && (
          <div className="src-grid mb-2">
            <span className="src-k">Expected</span>
            <span className="src-v" title={localWhen(info.reorder_due_time)}>
              {rel(info.reorder_due_time)}
            </span>
            <span className="src-k">Frequency</span>
            <span className="src-v">{daysLabel(info.reorder_due_days)}</span>
          </div>
        )
      )}
      {sent != null && sent.already_placed_order && (
        <div className="src-alert">Customer already placed a later order</div>
      )}
      {sent == null && (
        <div className={info != null ? 'src-actions' : 'src-actions mt-0'}>
          {info == null && (
            <button type="button" onClick={load} disabled={dueLoading}>
              {dueLoading ? 'Checking…' : 'When again?'}
            </button>
          )}
          <button type="button" onClick={send} disabled={sendLoading}>
            {sendLoading ? 'Sending…' : 'Send offer'}
          </button>
        </div>
      )}
      {dueFailed && <div className="src-tz mt-1">Couldn’t load reorder due</div>}
      {sendFailed && <div className="src-tz mt-1">Couldn’t send offer</div>}
    </div>
  )
}

function ProfilePanel(props: {
  profile: CustomerProfile
  locale?: string
  orderUuid?: string
  onOfferSent?: () => void
}): JSX.Element {
  const { profile, locale } = props
  const l: CustomerLoyalty | null = profile.loyalty
  const aovDelta = profile.customer_aov - profile.restaurant_aov
  const aovHint =
    aovDelta > 0.5 ? 'above restaurant avg' : aovDelta < -0.5 ? 'below restaurant avg' : 'near restaurant avg'

  return (
    <Panel id="customer-profile" label="Customer" headerExtra={typeChip(profile.customer_type)}>
      <li className="list-group-item">
        <div className="mkt-cols">
          <div className="src-grid">
          <span className="src-k">Orders</span>
          <span className="src-v">
            {typeof profile.order_count === 'number' ? profile.order_count.toLocaleString() : '—'}
          </span>
          <span className="src-k">AOV</span>
          <span className="src-v">
            {money(locale, profile.customer_aov)}
            <span className="src-tz">
              {' '}
              ({aovHint}: {money(locale, profile.restaurant_aov)})
            </span>
          </span>
          {l != null && (
            <>
              <span className="src-k mkt-loyalty">Loyalty</span>
              <span className="src-v" />
              <div className="mkt-indent src-grid">
                <span className="src-k">Balance</span>
                <span className="src-v">{pts(l.points_balance)}</span>
                <span className="src-k">Lifetime</span>
                <span className="src-v">{pts(l.life_time_points)}</span>
                {(l.points_earned_on_order > 0 || l.points_redeemed_on_order > 0) && (
                  <>
                    <span className="src-k">This order</span>
                    <span className="src-v">
                      {l.points_earned_on_order > 0 ? `+${pts(l.points_earned_on_order)}` : '0 pts'}
                      {l.points_redeemed_on_order > 0 ? ` · −${pts(l.points_redeemed_on_order)} redeemed` : ''}
                    </span>
                  </>
                )}
                {l.average_points_earned_per_order > 0 && (
                  <>
                    <span className="src-k">Avg / order</span>
                    <span className="src-v">{pts(l.average_points_earned_per_order)}</span>
                  </>
                )}
                {l.highest_reward_available != null && (
                  <>
                    <span className="src-k">Can redeem</span>
                    <span className="src-v">
                      {l.highest_reward_available.name}
                      <span className="src-tz"> ({pts(l.highest_reward_available.points_cost)})</span>
                    </span>
                  </>
                )}
                {l.next_reward != null && (
                  <>
                    <span className="src-k">Next reward</span>
                    <span className="src-v">
                      {l.next_reward.name}
                      {l.points_to_next_reward != null && (
                        <span className="src-tz"> ({pts(l.points_to_next_reward)} to go)</span>
                      )}
                    </span>
                  </>
                )}
                {l.next_reward == null && l.points_to_next_reward != null && (
                  <>
                    <span className="src-k">Next reward</span>
                    <span className="src-v">{pts(l.points_to_next_reward)} to go</span>
                  </>
                )}
              </div>
            </>
          )}
          </div>
          {props.orderUuid != null && (
            <div>
              <span className="src-k mkt-loyalty">Next order</span>
              <div className="mt-2">
                <ReorderDue orderUuid={props.orderUuid} onOfferSent={props.onOfferSent} />
              </div>
            </div>
          )}
        </div>
      </li>
    </Panel>
  )
}

function SnapshotRow(props: { o: OrderSnapshot; locale?: string; label?: string }): JSX.Element {
  const { o, locale } = props
  const items = (o.items || []).map((i) => i.name).join(' · ')
  const offer = offerLabel(o)
  const fromUuid = o.incentive && o.incentive.from_order_uuid
  const meta: string[] = []
  if (o.gap_from_previous_order > 0) {
    meta.push(`${o.gap_from_previous_order}d since last`)
  }
  if (o.loyalty_points_earned > 0) meta.push(`+${pts(o.loyalty_points_earned)}`)
  if (o.loyalty_points_redeemed > 0) meta.push(`−${pts(o.loyalty_points_redeemed)} redeemed`)

  return (
    <li className="list-group-item">
      <div className="src-head">
        <div>
          {props.label != null && <span className="src-chip src-chip-info">{props.label}</span>}
          {fromUuid != null && <span className="src-chip src-chip-info">Offer redeemed</span>}
          <span className="src-time">
            {o.day_of_week} · {humanize(o.time_of_day)} · {humanize(o.order_type)}
          </span>
        </div>
        <div className="src-name">{money(locale, o.total)}</div>
      </div>
      {items !== '' && <div className="src-v mt-1">{items}</div>}
      {meta.length > 0 && <div className="src-tz mt-1">{meta.join(' · ')}</div>}
      {offer != null && (
        <div className="src-v mt-1">
          {offer}
          {fromUuid != null && props.label == null && (
            <>
              {' '}
              <a href={`#/orders/${fromUuid}`}>View order ↗</a>
            </>
          )}
        </div>
      )}
    </li>
  )
}

function PastOrdersPanel(props: { past: PastOrdersResponse; locale?: string }): JSX.Element {
  const { past, locale } = props
  const rows = past.past_orders || []

  return (
    <Panel id="past-orders" label="Past orders">
      {past.current_order != null && (
        <SnapshotRow o={past.current_order} locale={locale} label="This order" />
      )}
      {rows.map((o: OrderSnapshot, idx: number) => (
        <SnapshotRow key={idx} o={o} locale={locale} />
      ))}
    </Panel>
  )
}

export default function Marketing(props: {
  incentives: IncentivesResult
  customer: CustomerMarketingResult
}): JSX.Element {
  const { incentives, customer } = props
  const order = useContext(OrderCtx)
  const restaurant = useContext(RestaurantCtx) as Restaurant | null
  const locale = restaurant ? restaurant.locale : undefined
  const selfUuid = order ? order.uuid : undefined
  const t = incentives.triggering
  const hasIncentives =
    t != null || (incentives.sent != null && incentives.sent.length > 0)

  return (
    <div>
      {customer.profile != null && (
        <div className="mkt-block">
          <ProfilePanel
            profile={customer.profile}
            locale={locale}
            orderUuid={selfUuid}
            onOfferSent={incentives.refetch}
          />
        </div>
      )}

      {hasIncentives && (
        <div className="mkt-flow">
          {t != null && (
            <div key="triggered-by">
              <Panel id="triggered-by" label="Order triggered by">
                <IncentiveBody i={t} selfUuid={selfUuid} />
              </Panel>
            </div>
          )}
          {incentives.sent != null &&
            incentives.sent.length == 0 &&
            t != null && (
              <div key="none-sent">
                <Panel id="incentives-sent" label="Offer sent">
                  <Line label="Incentives">None sent for this order</Line>
                </Panel>
              </div>
            )}
          {incentives.sent != null &&
            incentives.sent.map((i: OrderIncentive) => (
              <div key={i.id}>
                <Panel id={`incentive-${i.id}`} label="Offer sent" headerExtra={statusChip(i)}>
                  <IncentiveBody i={i} selfUuid={selfUuid} onRedeem={incentives.refetch} />
                </Panel>
              </div>
            ))}
        </div>
      )}

      {hasPastOrders(customer.past) && (
        <div className="mkt-block">
          <PastOrdersPanel past={customer.past} locale={locale} />
        </div>
      )}
    </div>
  )
}
